import React, { createContext, useContext, useState, useEffect } from 'react';
import serviceManager from '../services/serviceManager';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Get auth service from service manager
  const authService = serviceManager.services.auth;

  // Check if user is logged in on app start
  useEffect(() => {
    const checkAuth = async () => {
      try {
        if (authService.isAuthenticated()) {
          const currentUser = await authService.getCurrentUser();
          if (currentUser) {
            setUser(currentUser);
          }
        }
      } catch (error) {
        console.error('Error checking auth:', error);
        authService.removeToken();
      } finally {
        setLoading(false);
      }
    };

    checkAuth();
  }, [authService]);

  const login = async (email, password) => {
    try {
      setLoading(true);
      setError(null);

      const data = await authService.login(email, password);
      
      setUser(data.user);
      return { success: true, user: data.user };
    } catch (error) {
      setError(error.message || 'Error en el login');
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  };

  const register = async (userData) => {
    try {
      setLoading(true);
      setError(null);

      const data = await authService.register(userData);
      
      setUser(data.user);
      return { success: true, user: data.user };
    } catch (error) {
      setError(error.message || 'Error en el registro');
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      await authService.logout();
    } catch (error) {
      console.error('Error during logout:', error);
    } finally {
      setUser(null);
      setError(null);
    }
  };

  const updateUser = async (userData) => {
    try {
      const result = await authService.updateProfile(userData);
      if (result.success) {
        setUser(result.user);
      }
      return result;
    } catch (error) {
      setError(error.message || 'Error actualizando perfil');
      return { success: false, error: error.message };
    }
  };

  const changePassword = async (currentPassword, newPassword) => {
    try {
      setLoading(true);
      setError(null);

      const result = await authService.changePassword(currentPassword, newPassword);
      return result;
    } catch (error) {
      setError(error.message || 'Error cambiando contraseña');
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  };

  const forgotPassword = async (email) => {
    try {
      setLoading(true);
      setError(null);

      const result = await authService.forgotPassword(email);
      return result;
    } catch (error) {
      setError(error.message || 'Error enviando email de recuperación');
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  };

  const resetPassword = async (token, password) => {
    try {
      setLoading(true);
      setError(null);

      const result = await authService.resetPassword(token, password);
      return result;
    } catch (error) {
      setError(error.message || 'Error restableciendo contraseña');
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  };

  const verifyEmail = async (token) => {
    try {
      setLoading(true);
      setError(null);

      const result = await authService.verifyEmail(token);
      return result;
    } catch (error) {
      setError(error.message || 'Error verificando email');
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  };

  const clearError = () => {
    setError(null);
  };

  const hasRole = (role) => {
    return user && user.role === role;
  };

  const isAuthenticated = () => {
    return authService.isAuthenticated();
  };

  const getCurrentUser = async () => {
    try {
      return await authService.getCurrentUser();
    } catch (error) {
      console.error('Error getting current user:', error);
      return null;
    }
  };

  const value = {
    user,
    loading,
    error,
    login,
    register,
    logout,
    updateUser,
    changePassword,
    forgotPassword,
    resetPassword,
    verifyEmail,
    clearError,
    isAuthenticated,
    hasRole,
    getCurrentUser,
    // Service manager info
    mode: serviceManager.getCurrentMode(),
    isLocalMode: serviceManager.getCurrentMode() === 'local',
    isRealMode: serviceManager.getCurrentMode() === 'real'
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;